import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Button, Stack, Chip, Avatar, Divider } from "@mui/joy";
import DescriptionIcon from "@mui/icons-material/Description";
import EmailIcon from "@mui/icons-material/Email";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import "./BillOverview.css";
import BillProgress from "../BillProgress/BillProgress";
import RelatedBills from "../RelatedBills/RelatedBills";
import HeroImg from "../../unseen-histories-_4us-wUwO30-unsplash.jpg";
import avatarFallback from "../../Male_Avatar.jpg";

function SponsorRow({ name, party, state, imageUrl }) {
  return (
    <Stack direction="row" spacing="12px" alignItems="center">
      <Avatar
        src={imageUrl || avatarFallback}
        alt={name}
        sx={{ width: 40, height: 40, flexShrink: 0 }}
      />
      <Box>
        <Typography sx={{ fontSize: "16px", fontWeight: 600, lineHeight: "24px", color: "#0075b2" }}>
          {name}
        </Typography>
        <Typography sx={{ fontSize: "14px", fontWeight: 400, lineHeight: "22px", color: "#535862" }}>
          {[party, state].filter(Boolean).join(" • ")}
        </Typography>
      </Box>
    </Stack>
  );
}

function BillOverview({ bill, setOpen }) {
  const [cosponsorExpanded, setCosponsorExpanded] = useState(false);

  const readText = () => {
    window.open(bill?.text, "_blank", "noopener,noreferrer");
  };

  const lastActionRaw = bill?.actions && bill.actions.length > 0 ? bill.actions[0] : null;
  const lastActionText = typeof lastActionRaw === "string" ? lastActionRaw : lastActionRaw?.text;

  const cosponsors = bill?.cosponsors || [];
  const cosponsorCount = cosponsors.length;

  return (
    <Box>
      {/* ── HERO ── */}
      <Box sx={{ position: "relative", backgroundColor: "#003049", overflow: "hidden" }}>
        <Box
          component="img"
          src={HeroImg}
          alt=""
          aria-hidden="true"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: 0.2,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            maxWidth: "1200px",
            mx: "auto",
            px: { xs: "20px", md: "32px" },
            py: "40px",
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Breadcrumb */}
          <Box
            component="nav"
            aria-label="Breadcrumb"
            sx={{ mb: "24px" }}
          >
            <Box
              component="button"
              onClick={() => window.history.back()}
              sx={{
                background: "none",
                border: "none",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                color: "rgba(255,255,255,0.6)",
                fontSize: "14px",
                fontWeight: 500,
                padding: 0,
                "&:hover": { color: "white" },
              }}
            >
              ← Bills
            </Box>
          </Box>

          <Stack spacing="16px">
            {bill?.category && (
              <Typography
                component="p"
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#fdf0d5",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                {bill.category}
              </Typography>
            )}

            <Typography
              level="h1"
              sx={{
                fontSize: { xs: "32px", md: "56px" },
                fontWeight: 700,
                lineHeight: { xs: 1.25, md: 1.2 },
                color: "white",
              }}
            >
              {bill?.title}
            </Typography>

            {bill?.fullTitle && (
              <Typography
                sx={{ fontSize: "18px", fontWeight: 600, lineHeight: "26px", color: "white" }}
              >
                Official Legislation Title: {bill.fullTitle}
              </Typography>
            )}

            {bill?.description && (
              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: 400,
                  lineHeight: "26px",
                  color: "rgba(255,255,255,0.9)",
                  maxWidth: "720px",
                }}
              >
                {bill.description}
              </Typography>
            )}

            {bill?.text && (
              <Button
                startDecorator={<DescriptionIcon />}
                onClick={readText}
                sx={{
                  width: "fit-content",
                  backgroundColor: "white",
                  border: "1px solid #0075b2",
                  color: "#0075b2",
                  fontWeight: 600,
                  fontSize: "16px",
                  borderRadius: "6px",
                  minHeight: "48px",
                  px: "24px",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.92)" },
                }}
              >
                Read the full document
              </Button>
            )}
          </Stack>
        </Box>
      </Box>

      {/* ── CONTENT SECTION ── */}
      <Box sx={{ backgroundColor: "#003049", py: "40px" }}>
        <Box sx={{ maxWidth: "1200px", mx: "auto", px: { xs: "20px", md: "32px" } }}>

          {/* Two-column layout */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              gap: "32px",
              alignItems: "flex-start",
            }}
          >
            {/* ── LEFT COLUMN ── */}
            <Stack spacing="32px" sx={{ flex: 1, minWidth: 0 }}>

              {/* Bill progress */}
              <BillProgress actions={bill?.actions} />

              {/* Editorial prose: Who it impacts + Why it matters */}
              {((Array.isArray(bill?.whoItAffects) && bill.whoItAffects.length > 0) || bill?.whyItMatters) && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "36px" }}>

                  {bill?.whyItMatters && (
                    <Box>
                      <Typography
                        component="p"
                        sx={{
                          fontSize: "11px",
                          fontWeight: 700,
                          color: "#fdf0d5",
                          textTransform: "uppercase",
                          letterSpacing: "0.12em",
                          mb: "14px",
                        }}
                      >
                        Why it matters
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "18px",
                          fontWeight: 400,
                          lineHeight: "30px",
                          color: "rgba(255,255,255,0.9)",
                        }}
                      >
                        {bill.whyItMatters}
                      </Typography>
                    </Box>
                  )}

                  {Array.isArray(bill?.whoItAffects) && bill.whoItAffects.length > 0 && (
                    <Box>
                      <Typography
                        component="p"
                        sx={{
                          fontSize: "11px",
                          fontWeight: 700,
                          color: "#fdf0d5",
                          textTransform: "uppercase",
                          letterSpacing: "0.12em",
                          mb: "14px",
                        }}
                      >
                        Who it impacts
                      </Typography>
                      <Box
                        component="ul"
                        sx={{
                          margin: 0,
                          paddingLeft: "20px",
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                        }}
                      >
                        {bill.whoItAffects.map((group, i) => (
                          <Box
                            key={i}
                            component="li"
                            sx={{
                              fontSize: "18px",
                              fontWeight: 400,
                              lineHeight: "30px",
                              color: "rgba(255,255,255,0.9)",
                            }}
                          >
                            {group}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* Split panel: Supporters / Opposition */}
              {(bill?.support || bill?.opposition) && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    borderRadius: "4px",
                    overflow: "hidden",
                  }}
                >
                  {bill?.support && (
                    <Box
                      sx={{
                        backgroundColor: "#f0f4f0",
                        padding: "28px 24px",
                        borderRight: { sm: "1px solid rgba(0,0,0,0.06)" },
                        borderBottom: { xs: "1px solid rgba(0,0,0,0.06)", sm: "none" },
                      }}
                    >
                      <Typography
                        component="p"
                        sx={{
                          fontSize: "11px",
                          fontWeight: 700,
                          color: "#2d5a2d",
                          textTransform: "uppercase",
                          letterSpacing: "0.1em",
                          mb: "14px",
                        }}
                      >
                        What supporters say
                      </Typography>
                      <Typography sx={{ fontSize: "17px", lineHeight: "28px", color: "#1a2e1a" }}>
                        {bill.support}
                      </Typography>
                    </Box>
                  )}

                  {bill?.opposition && (
                    <Box
                      sx={{
                        backgroundColor: "#fdf1f1",
                        padding: "28px 24px",
                      }}
                    >
                      <Typography
                        component="p"
                        sx={{
                          fontSize: "11px",
                          fontWeight: 700,
                          color: "#7a2d2d",
                          textTransform: "uppercase",
                          letterSpacing: "0.1em",
                          mb: "14px",
                        }}
                      >
                        What the opposition says
                      </Typography>
                      <Typography sx={{ fontSize: "17px", lineHeight: "28px", color: "#2e1a1a" }}>
                        {bill.opposition}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Related Bills */}
              {bill?.relatedBills?.length > 0 && (
                <Box sx={{ pt: "8px" }}>
                  <RelatedBills relatedBills={bill.relatedBills} />
                </Box>
              )}
            </Stack>

            {/* ── RIGHT COLUMN (sticky) ── */}
            <Box
              sx={{
                width: { lg: "340px" },
                flexShrink: 0,
                position: { lg: "sticky" },
                top: { lg: "96px" },
                alignSelf: { lg: "flex-start" },
              }}
            >
              <Box
                sx={{
                  backgroundColor: "white",
                  borderRadius: "4px",
                  boxShadow: "0px 1px 1px rgba(21,21,21,0.08), 0px 2px 2px rgba(21,21,21,0.08)",
                  overflow: "hidden",
                }}
              >
                {/* CTA */}
                <Box sx={{ padding: "24px" }}>
                  {bill?.bill_id && (
                    <Chip
                      size="sm"
                      sx={{
                        backgroundColor: "#e5e7eb",
                        color: "#374151",
                        fontWeight: 500,
                        fontSize: "12px",
                        borderRadius: "24px",
                        border: "none",
                        mb: "10px",
                      }}
                    >
                      {bill.bill_id}
                    </Chip>
                  )}
                  <Typography
                    sx={{
                      fontSize: "22px",
                      fontWeight: 600,
                      lineHeight: "30px",
                      color: "#171a1c",
                      mb: "8px",
                    }}
                  >
                    Tell Congress where you stand
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "15px",
                      lineHeight: "24px",
                      color: "#32383e",
                      mb: "20px",
                    }}
                  >
                    Contact your representative directly and let them know how this bill affects you.
                  </Typography>
                  <Button
                    fullWidth
                    size="lg"
                    startDecorator={<EmailIcon />}
                    onClick={() => setOpen(true)}
                    sx={{
                      backgroundColor: "#0075b2",
                      color: "white",
                      fontWeight: 600,
                      fontSize: "16px",
                      borderRadius: "6px",
                      minHeight: "48px",
                      "&:hover": { backgroundColor: "#005c8e" },
                    }}
                  >
                    Contact your representative
                  </Button>
                </Box>

                {/* Current status */}
                {lastActionText && (
                  <>
                    <Divider />
                    <Box sx={{ padding: "20px 24px" }}>
                      <Typography
                        component="p"
                        sx={{
                          fontSize: "11px",
                          fontWeight: 700,
                          color: "#9ca3af",
                          textTransform: "uppercase",
                          letterSpacing: "0.1em",
                          mb: "8px",
                        }}
                      >
                        Current status
                      </Typography>
                      <Typography sx={{ fontSize: "15px", lineHeight: "24px", color: "#32383e" }}>
                        {lastActionText}
                      </Typography>
                    </Box>
                  </>
                )}

                {/* Primary sponsor + collapsible cosponsors */}
                {bill?.sponsor?.name && (
                  <>
                    <Divider />
                    <Box sx={{ padding: "20px 24px" }}>
                      <Typography
                        component="p"
                        sx={{
                          fontSize: "11px",
                          fontWeight: 700,
                          color: "#9ca3af",
                          textTransform: "uppercase",
                          letterSpacing: "0.1em",
                          mb: "12px",
                        }}
                      >
                        Primary sponsor
                      </Typography>
                      <SponsorRow
                        name={bill.sponsor.name}
                        party={bill.sponsor.party}
                        state={bill.sponsor.state}
                        imageUrl={bill.sponsor.imageUrl}
                      />

                      {cosponsorCount > 0 && (
                        <>
                          {cosponsorExpanded && (
                            <Stack spacing="12px" sx={{ mt: "12px" }}>
                              {cosponsors.slice(0, 5).map((cosponsor, index) => (
                                <SponsorRow
                                  key={index}
                                  name={cosponsor.name}
                                  party={cosponsor.party}
                                  state={cosponsor.state}
                                  imageUrl={cosponsor.authorImage}
                                />
                              ))}
                              {cosponsorCount > 5 && (
                                <Typography sx={{ fontSize: "13px", color: "#9ca3af" }}>
                                  +{cosponsorCount - 5} more cosponsor{cosponsorCount - 5 !== 1 ? "s" : ""}
                                </Typography>
                              )}
                            </Stack>
                          )}
                          <Button
                            variant="plain"
                            size="sm"
                            endDecorator={cosponsorExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            onClick={() => setCosponsorExpanded(p => !p)}
                            sx={{
                              mt: "10px",
                              color: "#0075b2",
                              fontWeight: 600,
                              fontSize: "13px",
                              px: 0,
                              "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
                            }}
                          >
                            {cosponsorExpanded
                              ? "Show less"
                              : `+${cosponsorCount} cosponsor${cosponsorCount !== 1 ? "s" : ""}`}
                          </Button>
                        </>
                      )}
                    </Box>
                  </>
                )}

              </Box>
            </Box>
          </Box>


        </Box>
      </Box>
    </Box>
  );
}

BillOverview.propTypes = {
  bill: PropTypes.object.isRequired,
  setOpen: PropTypes.func.isRequired,
};

SponsorRow.propTypes = {
  name: PropTypes.string,
  party: PropTypes.string,
  state: PropTypes.string,
  imageUrl: PropTypes.string,
};

export default BillOverview;
